/**
 * This app's printer host for `@rista/printing` — registered once in `bootstrapCoreBridges.ts`.
 *
 * `snapshot` is only what the shared adapter cannot read itself: device storage, the LAN role and
 * the operator's permitted payment modes. Each field is ONE existing app helper, called — not copied.
 */
import type { PrintHost } from "@rista/core/printing";
import type { UnprintedSales } from "@rista/printing";

import { getUnprintedTicketsFn } from "facade/ServiceFacade";
import { getLocalStorage } from "utils";
import { isAndroid } from "utils/nativeHooks";
import { LOCALSTORAGE_KEYS } from "utils/constants";
import { USBPrinter, getAddedBlePrintersList, getLocalStorageWithParse } from "utils/localStorage";
import { isSlave } from "helper/localServer";
import { isPineLabDevice } from "helper/commonHelpers";
import { getPaymentMethods } from "helper/paymenthelper";
import { webTransport } from "./transport";
import { webRasterizer } from "./raster";

// No `gapMs` override: the package's 500 ms default is what spaces jobs here. Electron's main
// process and the NW shell do keep 500 ms queues of their own, but those only wait when a job is
// already queued BEHIND the one printing — v1 pushed every job at once, so its jobs were spaced;
// the engine sends one job and awaits it, so those queues never hold two and never wait. With a
// 0 gap the jobs reached the printer 1 ms apart ("on the wire", legacyParity.test.ts).
export const webPrintHost: PrintHost = {
  transport: webTransport,
  raster: webRasterizer,

  /**
   * v1 `utils/polling.js` `createDelayedTasks` — the same facade call, which already picks the
   * online saga or the offline store and normalises both to `{ ticketSales, itemLabelSales }`.
   * A rejection carries the API error, whose `code` is how the poller sees
   * `REST_TICKET_POLLING_UNAUTHORIZED`.
   */
  fetchUnprinted: (request) =>
    new Promise<UnprintedSales>((resolve, reject) => {
      getUnprintedTicketsFn({
        dataToSend: { pageSize: request.pageSize },
        ticketsToggle: request.tickets,
        labelsToggle: request.labels,
        callback: (answer: any) => resolve(answer || {}),
        errorCallback: reject,
      });
    }),

  snapshot: () => ({
    deviceId: getLocalStorage(LOCALSTORAGE_KEYS.deviceIdentifier) || "",
    isSlave: isSlave(),
    printBillFromMaster: !!getLocalStorageWithParse(LOCALSTORAGE_KEYS.printBillFromMaster),
    ble: getAddedBlePrintersList(),
    usb: USBPrinter(),
    paymentMethods: getPaymentMethods() || [],
    // v1 hands a Pine Labs terminal the whole sale instead of printing to a printer; saying so here
    // is what keeps its bills and tickets off a printer they were never sent to
    builtInPrinter: isPineLabDevice(),
    // v1 blanks the QR, footer, signature and header logo before printing through the wrapper —
    // and never kicks the cash drawer there either
    canPrintImages: !isAndroid(),
  }),

  // `notifySkip` is deliberately NOT wired yet: v1's warning modal and "select a printer" toast are
  // POS messages, and the only caller today is the KIOSK, where a customer must never be shown one.
  // It lands with the POS convergence — docs/flows/PRINTING.md §10 (S4).
};
